import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Plus, Edit, Trash2, Car, Search, Filter,
  Users, Fuel, Settings2, Upload, FileText, X, Package, Loader2
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Badge } from '../../components/ui/badge';
import VehicleAddonsManager from '../../components/admin/VehicleAddonsManager';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminVehicles = () => {
  const { token } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [addonsDialogOpen, setAddonsDialogOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [locations, setLocations] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    brand: '',
    model: '',
    year: new Date().getFullYear(),
    category: 'economy',
    description_en: '',
    description_fr: '',
    features_en: '',
    features_fr: '',
    seats: 5,
    doors: 4,
    transmission: 'automatic',
    fuel_type: 'petrol',
    images: '',
    base_daily_rate: 100,
    deposit_amount: 1500,
    daily_km_limit: 300,
    price_per_extra_km: 0.50,
    available: true,
    // Private fields for contracts
    plate_number: '',
    vin: '',
    color: '',
    first_registration: '',
    insurance_company: '',
    insurance_policy: ''
  });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchVehicles = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/vehicles`, { headers });
      setVehicles(response.data);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      toast.error('Failed to load vehicles');
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/locations`);
      setLocations(response.data);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  useEffect(() => {
    fetchVehicles();
    fetchLocations();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const openCreateDialog = () => {
    setSelectedVehicle(null);
    setFormData({
      name: '',
      brand: '',
      model: '',
      year: new Date().getFullYear(),
      category: 'economy',
      description_en: '',
      description_fr: '',
      features_en: '',
      features_fr: '',
      seats: 5,
      doors: 4,
      transmission: 'automatic',
      fuel_type: 'petrol',
      images: '',
      base_daily_rate: 100,
      deposit_amount: 1500,
      available: true,
      // Private fields
      plate_number: '',
      vin: '',
      color: '',
      first_registration: '',
      insurance_company: '',
      insurance_policy: ''
    });
    setDialogOpen(true);
  };

  const openEditDialog = (vehicle) => {
    setSelectedVehicle(vehicle);
    setFormData({
      ...vehicle,
      features_en: vehicle.features_en?.join(', ') || '',
      features_fr: vehicle.features_fr?.join(', ') || '',
      images: vehicle.images?.join(', ') || '',
      deposit_amount: vehicle.deposit_amount || 1500,
      // Private fields (with defaults)
      plate_number: vehicle.plate_number || '',
      vin: vehicle.vin || '',
      color: vehicle.color || '',
      first_registration: vehicle.first_registration || '',
      insurance_company: vehicle.insurance_company || '',
      insurance_policy: vehicle.insurance_policy || ''
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      ...formData,
      year: parseInt(formData.year),
      seats: parseInt(formData.seats),
      doors: parseInt(formData.doors),
      base_daily_rate: parseFloat(formData.base_daily_rate),
      deposit_amount: parseFloat(formData.deposit_amount),
      features_en: formData.features_en.split(',').map(f => f.trim()).filter(Boolean),
      features_fr: formData.features_fr.split(',').map(f => f.trim()).filter(Boolean),
      images: formData.images.split(',').map(i => i.trim()).filter(Boolean),
      pricing_tiers: [
        { min_days: 1, max_days: 2, daily_rate: parseFloat(formData.base_daily_rate) },
        { min_days: 3, max_days: 6, daily_rate: parseFloat(formData.base_daily_rate) * 0.9 },
        { min_days: 7, max_days: 13, daily_rate: parseFloat(formData.base_daily_rate) * 0.8 },
        { min_days: 14, max_days: 999, daily_rate: parseFloat(formData.base_daily_rate) * 0.7 }
      ],
      // Private fields
      plate_number: formData.plate_number,
      vin: formData.vin,
      color: formData.color,
      first_registration: formData.first_registration,
      insurance_company: formData.insurance_company,
      insurance_policy: formData.insurance_policy
    };

    try {
      if (selectedVehicle) {
        await axios.put(`${API_URL}/api/admin/vehicles/${selectedVehicle.id}`, payload, { headers });
        toast.success('Vehicle updated successfully');
      } else {
        await axios.post(`${API_URL}/api/admin/vehicles`, payload, { headers });
        toast.success('Vehicle created successfully');
      }
      setDialogOpen(false);
      fetchVehicles();
    } catch (error) {
      console.error('Error saving vehicle:', error);
      toast.error('Failed to save vehicle');
    }
  };

  const handleDelete = async () => {
    if (!selectedVehicle) return;
    
    try {
      await axios.delete(`${API_URL}/api/admin/vehicles/${selectedVehicle.id}`, { headers });
      toast.success('Vehicle deleted successfully');
      setDeleteDialogOpen(false);
      fetchVehicles();
    } catch (error) {
      console.error('Error deleting vehicle:', error);
      toast.error('Failed to delete vehicle');
    }
  };

  const openAddonsDialog = (vehicle) => {
    setSelectedVehicle(vehicle);
    setAddonsDialogOpen(true);
  };

  const filteredVehicles = vehicles.filter(v => {
    const matchesSearch = v.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         v.brand.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || v.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const categories = ['suv', 'citadine', 'utilitaire', 'premium'];

  return (
    <div className="space-y-6" data-testid="admin-vehicles">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-3xl text-[#181B1F] font-light">Vehicles</h1>
          <p className="text-gray-500 mt-1">Manage your vehicle fleet</p>
        </div>
        <Button onClick={openCreateDialog} className="btn-primary" data-testid="add-vehicle-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Vehicle
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search vehicles..."
            className="input-default pl-10"
            data-testid="search-vehicles"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="input-default w-40" data-testid="filter-category">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent className="bg-white border-gray-200">
            <SelectItem value="all" className="text-[#181B1F]">All Categories</SelectItem>
            {categories.map(cat => (
              <SelectItem key={cat} value={cat} className="text-[#181B1F] capitalize">
                {cat}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Vehicles Grid */}
      {loading ? (
        <div className="text-center py-12">
          <p className="text-gray-500">Loading vehicles...</p>
        </div>
      ) : filteredVehicles.length === 0 ? (
        <div className="text-center py-12">
          <Car className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No vehicles found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredVehicles.map((vehicle, index) => (
            <motion.div
              key={vehicle.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white border border-gray-100 rounded-sm overflow-hidden group"
            >
              <div className="aspect-video relative">
                <img
                  src={vehicle.images?.[0] || 'https://images.unsplash.com/photo-1692892529205-9f70ecb1df1d?w=400'}
                  alt={vehicle.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-3 left-3">
                  <Badge className={vehicle.available ? 'bg-green-500/80' : 'bg-red-500/80'}>
                    {vehicle.available ? 'Available' : 'Unavailable'}
                  </Badge>
                </div>
              </div>
              <div className="p-4 space-y-3">
                <div>
                  <p className="text-xs uppercase tracking-wider text-primary">{vehicle.category}</p>
                  <h3 className="text-[#181B1F] font-medium">{vehicle.name}</h3>
                  <p className="text-gray-400 text-sm">{vehicle.brand} • {vehicle.year}</p>
                </div>
                <div className="flex items-center gap-4 text-gray-400 text-sm">
                  <span className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    {vehicle.seats}
                  </span>
                  <span className="flex items-center gap-1">
                    <Settings2 className="w-4 h-4" />
                    {vehicle.transmission}
                  </span>
                  <span className="flex items-center gap-1">
                    <Fuel className="w-4 h-4" />
                    {vehicle.fuel_type}
                  </span>
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                  <span className="text-[#181B1F] font-medium">CHF {vehicle.base_daily_rate}/day</span>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openAddonsDialog(vehicle)}
                      className="text-primary hover:text-primary/80"
                      title="Manage Booking Options"
                      data-testid={`addons-vehicle-${vehicle.id}`}
                    >
                      <Package className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openEditDialog(vehicle)}
                      className="text-gray-500 hover:text-[#181B1F]"
                      data-testid={`edit-vehicle-${vehicle.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setSelectedVehicle(vehicle);
                        setDeleteDialogOpen(true);
                      }}
                      className="text-red-400 hover:text-red-300"
                      data-testid={`delete-vehicle-${vehicle.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border-gray-200 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#181B1F]">
              {selectedVehicle ? 'Edit Vehicle' : 'Add New Vehicle'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-500">Name *</Label>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="input-default"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-500">Brand *</Label>
                <Input
                  name="brand"
                  value={formData.brand}
                  onChange={handleInputChange}
                  className="input-default"
                  required
                />
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-500">Model *</Label>
                <Input
                  name="model"
                  value={formData.model}
                  onChange={handleInputChange}
                  className="input-default"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-500">Year *</Label>
                <Input
                  name="year"
                  type="number"
                  value={formData.year}
                  onChange={handleInputChange}
                  className="input-default"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-500">Category *</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData(prev => ({ ...prev, category: v }))}>
                  <SelectTrigger className="input-default">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat} className="text-[#181B1F] capitalize">
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-500">Seats</Label>
                <Input
                  name="seats"
                  type="number"
                  value={formData.seats}
                  onChange={handleInputChange}
                  className="input-default"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-500">Doors</Label>
                <Input
                  name="doors"
                  type="number"
                  value={formData.doors}
                  onChange={handleInputChange}
                  className="input-default"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-500">Transmission</Label>
                <Select value={formData.transmission} onValueChange={(v) => setFormData(prev => ({ ...prev, transmission: v }))}>
                  <SelectTrigger className="input-default">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    <SelectItem value="automatic" className="text-[#181B1F]">Automatic</SelectItem>
                    <SelectItem value="manual" className="text-[#181B1F]">Manual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-500">Fuel Type</Label>
                <Select value={formData.fuel_type} onValueChange={(v) => setFormData(prev => ({ ...prev, fuel_type: v }))}>
                  <SelectTrigger className="input-default">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    <SelectItem value="petrol" className="text-[#181B1F]">Petrol</SelectItem>
                    <SelectItem value="diesel" className="text-[#181B1F]">Diesel</SelectItem>
                    <SelectItem value="electric" className="text-[#181B1F]">Electric</SelectItem>
                    <SelectItem value="hybrid" className="text-[#181B1F]">Hybrid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-500">Base Daily Rate (CHF) *</Label>
              <Input
                name="base_daily_rate"
                type="number"
                step="0.01"
                value={formData.base_daily_rate}
                onChange={handleInputChange}
                className="input-default"
                required
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-500">Security Deposit (CHF) *</Label>
              <Input
                name="deposit_amount"
                type="number"
                step="0.01"
                value={formData.deposit_amount}
                onChange={handleInputChange}
                className="input-default"
                required
                data-testid="deposit-amount-input"
              />
              <p className="text-gray-400 text-xs">Pre-authorized at checkout, not charged until admin captures</p>
            </div>

            {/* Kilometer Settings */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-500">Daily KM Limit</Label>
                <Input
                  name="daily_km_limit"
                  type="number"
                  value={formData.daily_km_limit || 300}
                  onChange={handleInputChange}
                  className="input-default"
                  data-testid="daily-km-limit-input"
                />
                <p className="text-gray-400 text-xs">Included kilometers per day</p>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-500">Price per Extra KM (CHF)</Label>
                <Input
                  name="price_per_extra_km"
                  type="number"
                  step="0.01"
                  value={formData.price_per_extra_km || 0.50}
                  onChange={handleInputChange}
                  className="input-default"
                  data-testid="price-per-extra-km-input"
                />
                <p className="text-gray-400 text-xs">Charged for km beyond daily limit</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-500">Description (English)</Label>
              <Textarea
                name="description_en"
                value={formData.description_en}
                onChange={handleInputChange}
                className="input-default"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-500">Description (French)</Label>
              <Textarea
                name="description_fr"
                value={formData.description_fr}
                onChange={handleInputChange}
                className="input-default"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-500">Features English (comma-separated)</Label>
              <Input
                name="features_en"
                value={formData.features_en}
                onChange={handleInputChange}
                className="input-default"
                placeholder="Leather seats, GPS, Bluetooth"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-500">Features French (comma-separated)</Label>
              <Input
                name="features_fr"
                value={formData.features_fr}
                onChange={handleInputChange}
                className="input-default"
                placeholder="Sièges en cuir, GPS, Bluetooth"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-500">Image URLs (comma-separated)</Label>
              <div className="flex gap-2">
                <Input
                  name="images"
                  value={formData.images}
                  onChange={handleInputChange}
                  className="input-default flex-1"
                  placeholder="https://example.com/image1.jpg, https://example.com/image2.jpg"
                />
              </div>
              <p className="text-gray-400 text-xs">
                Entrez les URLs des images séparées par des virgules
              </p>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                name="available"
                checked={formData.available}
                onChange={handleInputChange}
                className="rounded border-gray-300"
              />
              <Label className="text-gray-500">Available for booking</Label>
            </div>

            {/* Private Vehicle Details Section */}
            <div className="border-t border-gray-200 pt-4 mt-4">
              <h3 className="text-[#181B1F] font-medium mb-4 flex items-center gap-2">
                <span className="text-yellow-400">🔒</span>
                Contract Details (Internal Only)
              </h3>
              <p className="text-gray-400 text-sm mb-4">
                These details are used in rental contracts and are not visible to customers.
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-500">Plate Number</Label>
                  <Input
                    name="plate_number"
                    value={formData.plate_number}
                    onChange={handleInputChange}
                    className="input-default"
                    placeholder="GE 123456"
                    data-testid="plate-number-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-500">VIN (Chassis Number)</Label>
                  <Input
                    name="vin"
                    value={formData.vin}
                    onChange={handleInputChange}
                    className="input-default"
                    placeholder="WVWZZZ3CZWE123456"
                    data-testid="vin-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-gray-500">Color</Label>
                  <Input
                    name="color"
                    value={formData.color}
                    onChange={handleInputChange}
                    className="input-default"
                    placeholder="Noir / Black"
                    data-testid="color-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-500">First Registration Date</Label>
                  <Input
                    name="first_registration"
                    value={formData.first_registration}
                    onChange={handleInputChange}
                    className="input-default"
                    placeholder="2024-01-15"
                    data-testid="first-registration-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-gray-500">Insurance Company</Label>
                  <Input
                    name="insurance_company"
                    value={formData.insurance_company}
                    onChange={handleInputChange}
                    className="input-default"
                    placeholder="AXA Switzerland"
                    data-testid="insurance-company-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-500">Insurance Policy Number</Label>
                  <Input
                    name="insurance_policy"
                    value={formData.insurance_policy}
                    onChange={handleInputChange}
                    className="input-default"
                    placeholder="POL-2024-123456"
                    data-testid="insurance-policy-input"
                  />
                </div>
              </div>

              {/* Vehicle Documents Upload Section */}
              {selectedVehicle && (
                <VehicleDocumentsUpload 
                  vehicleId={selectedVehicle.id}
                  registrationUrl={selectedVehicle.registration_document_url}
                  insuranceUrl={selectedVehicle.insurance_certificate_url}
                  token={token}
                  onUpload={fetchVehicles}
                />
              )}
            </div>

            <div className="flex justify-end gap-4 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="btn-outline">
                Cancel
              </Button>
              <Button type="submit" className="btn-primary">
                {selectedVehicle ? 'Update Vehicle' : 'Create Vehicle'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white border-gray-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#181B1F]">Delete Vehicle</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-500">
              Are you sure you want to delete "{selectedVehicle?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="btn-outline">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600 text-[#181B1F]">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add-ons Management Dialog */}
      <Dialog open={addonsDialogOpen} onOpenChange={setAddonsDialogOpen}>
        <DialogContent className="bg-white border-gray-200 max-w-xl max-h-[85vh] overflow-y-auto" data-testid="addons-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#181B1F] flex items-center gap-2">
              <Package className="w-6 h-6 text-primary" />
              Booking Options
            </DialogTitle>
          </DialogHeader>
          {selectedVehicle && addonsDialogOpen && (
            <VehicleAddonsManager
              vehicleId={selectedVehicle.id}
              vehicleName={selectedVehicle.name}
              token={token}
              onClose={() => setAddonsDialogOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Vehicle Documents Upload Component
const VehicleDocumentsUpload = ({ vehicleId, registrationUrl, insuranceUrl, token, onUpload }) => {
  const registrationInputRef = useRef(null);
  const insuranceInputRef = useRef(null);
  const [uploading, setUploading] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const handleUpload = async (file, documentType) => {
    setUploading(documentType);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', documentType);

    try {
      await axios.post(
        `${API_URL}/api/admin/vehicles/${vehicleId}/documents`,
        formData,
        { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(`${documentType === 'registration' ? 'Registration document' : 'Insurance certificate'} uploaded`);
      onUpload();
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error('Failed to upload document');
    } finally {
      setUploading(null);
    }
  };

  const handleDelete = async (documentType) => {
    try {
      await axios.delete(
        `${API_URL}/api/admin/vehicles/${vehicleId}/documents/${documentType}`,
        { headers }
      );
      toast.success('Document removed');
      onUpload();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Failed to remove document');
    }
  };

  const DocumentItem = ({ label, labelFr, url, type, inputRef }) => (
    <div className="flex items-center justify-between p-3 bg-gray-100 rounded-lg border border-gray-200">
      <div className="flex items-center gap-3">
        <FileText className="w-5 h-5 text-[#4A945D]" />
        <div>
          <p className="text-[#181B1F] text-sm font-medium">{label}</p>
          <p className="text-gray-500 text-xs">{labelFr}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {url ? (
          <>
            <a 
              href={`${API_URL}${url}`} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#4A945D] hover:underline text-sm font-medium"
            >
              Voir
            </a>
            <button
              onClick={() => handleDelete(type)}
              className="p-1 text-red-500 hover:text-red-600 hover:bg-red-50 rounded"
            >
              <X className="w-4 h-4" />
            </button>
          </>
        ) : (
          <Button
            size="sm"
            variant="outline"
            onClick={() => inputRef.current?.click()}
            disabled={uploading === type}
            className="h-8 px-3 border-[#4A945D] text-[#4A945D] hover:bg-[#4A945D] hover:text-white text-xs"
          >
            <Upload className="w-3 h-3 mr-1" />
            {uploading === type ? 'Upload...' : 'Upload'}
          </Button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          className="hidden"
          onChange={(e) => {
            if (e.target.files?.[0]) {
              handleUpload(e.target.files[0], type);
              e.target.value = '';
            }
          }}
        />
      </div>
    </div>
  );

  return (
    <div className="mt-6 pt-4 border-t border-gray-200">
      <h4 className="text-[#181B1F] font-medium mb-3 flex items-center gap-2">
        <FileText className="w-4 h-4 text-[#4A945D]" />
        Documents du Véhicule
      </h4>
      <p className="text-gray-500 text-sm mb-3">
        Uploadez la carte grise et le certificat d'assurance. Ces documents seront joints aux procès-verbaux.
      </p>
      <div className="space-y-3">
        <DocumentItem 
          label="Vehicle Registration" 
          labelFr="Carte Grise"
          url={registrationUrl}
          type="registration"
          inputRef={registrationInputRef}
        />
        <DocumentItem 
          label="Insurance Certificate" 
          labelFr="Certificat d'Assurance"
          url={insuranceUrl}
          type="insurance"
          inputRef={insuranceInputRef}
        />
      </div>
    </div>
  );
};

export default AdminVehicles;
